import { Box, HStack, Icon, IconButton, Spinner, Text, Textarea, VStack } from "@chakra-ui/react";
import React, {useLayoutEffect, useRef, useState} from "react";
import { FaArrowUp } from "react-icons/fa";
import { continueSession } from "services/AgentChatService";
import { AnalysisTreeNodeProto, AnalysisTreeProto } from "build-ts/analysis";
import { AnalysisTreeSessionKey } from "build-ts/stateful_api";
import ReasoningTreeContainer from "containers/agentChat/ReasoningTreeContainer";
import { Header } from "build-ts/metadata";
import { ScenarioResultProto, ScenariosProcessingOutput } from "build-ts/schedule";
import { useAgentChat } from "context/AgentChatContext";
import MessageComponent from "./MessageComponent";
import { ArtifactProto, ChatMessageProto, ChatResponse } from "build-ts/chat_session";
import { ChatMessageType, chatMessageTypeFromJSON, DarwinHeader } from "build-ts/common";

interface ChatProps {
  metadataId: string | undefined;
  userId: string | undefined;
  chatSessionId: string | undefined;
  trees: AnalysisTreeProto[];
  treeSessions: AnalysisTreeSessionKey[];
  isLoadingChat: boolean;
	chatMessages: ChatMessage[];
	setChatMessages: React.Dispatch<React.SetStateAction<ChatMessage[]>>;
  nodeDataMap: Map<string, AnalysisTreeNodeProto | null>;
  setNodeDataMap: React.Dispatch<React.SetStateAction<Map<string, AnalysisTreeNodeProto | null>>>;
  // Initial reasoning tree data
  treeHeaders?: Header[];
  scenarioResults?: ScenarioResultProto[];
  synthesisResult?: ScenariosProcessingOutput;
  contextualisationResult?: ScenariosProcessingOutput;
  onTableClick?: (nodeId: string) => void;
  onFindingsClick?: (scenarioId: number) => void;
  onSynthesisClick?: () => void;
  onContextualizationClick?: () => void;
  // Session mode (EvaPlayground)
  isSessionMode?: boolean;
  sessionId?: string;
  // Chat sessions list and fetch function - used to refresh the list when a new chat session is created
  chatSessionsList?: DarwinHeader[];
  fetchChatSessionsList?: () => Promise<void>;
  isFromEvaPlayground?: boolean;
};

export interface ChatMessage {
  author: "user" | "ai" | "reasoning";
  content: string;
  artifacts?: ArtifactProto[] | undefined;
}

const Chat: React.FC<ChatProps> = ({
  metadataId,
  userId,
  trees,
  treeSessions,
  chatSessionId,
  isLoadingChat,
	chatMessages,
	setChatMessages,
	nodeDataMap,
	setNodeDataMap,
  // Initial reasoning tree props
  treeHeaders,
  scenarioResults,
  synthesisResult,
  contextualisationResult,
  onTableClick,
  onFindingsClick,
  onSynthesisClick,
  onContextualizationClick,
  isSessionMode = false,
  sessionId,
  // Chat sessions list and fetch function - used to refresh the list when a new chat session is created
  chatSessionsList = [],
  fetchChatSessionsList,
  isFromEvaPlayground = false,
 }) => {
  const [isLoading, setIsLoading] = useState(false);
	const [inputMessage, setInputMessage] = useState<string>("");
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const userScrolledRef = useRef(false);

  const { chatId } = useAgentChat();

  const handleScroll = () => {
    if (messagesContainerRef.current) {
      const container = messagesContainerRef.current;
      // Check if user is near bottom (within 100px)
      const isNearBottom =
        container.scrollHeight - container.scrollTop - container.clientHeight < 50;

      // Only set userScrolledRef if user actively scrolled away from bottom
      if (!isNearBottom) {
        userScrolledRef.current = true;
      }
    }
  };

  const scrollToBottom = () => {
    if (messagesContainerRef.current && !userScrolledRef.current) {
      const container = messagesContainerRef.current;
      container.scrollTop = container.scrollHeight;
    }
  };

  // Auto-scroll only when new messages added AND user hasn't manually scrolled away
  useLayoutEffect(() => {
    if (!isLoadingChat && chatMessages.length > 0) {
      scrollToBottom();
    }
  }, [chatMessages, isLoadingChat]);

	const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) =>{
		setInputMessage(e.target.value);
	};

	const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) =>{
		if(e.key === "Enter" && !e.shiftKey){
			e.preventDefault();
			handleSendMessage();
		}
	};

  const handleSendMessage = async () => {
		if(inputMessage.trim() !== "" && chatId) {
      const inputMsg =  inputMessage;
      userScrolledRef.current = false;
			const userMsg = { content: inputMessage, author: "user" as const};
			const loadingMsg = { content: "", author: "ai" as const};

			// Add user message and loading AI message
			setChatMessages(prev => [...prev, userMsg, loadingMsg]);
      setInputMessage("");
      setIsLoading(true);

			try {
				let response: ChatResponse | undefined;
				if (isSessionMode && sessionId) {
					response = await continueSession(sessionId, inputMsg);
				} else {
          response = await continueSession(chatId!, inputMsg);
				}

				// Remove the loading message and add the actual AI response
				setChatMessages(prev => {
					const updated = prev.slice(0, -1);

					if(response && response.messages) {
            // Then add the AI response
            if (isSessionMode) {
              // Eva Playground: messages use message_type instead of author
              for (let i = response.messages.length - 1; i >= 0; i--) {
                const msg: any = response.messages[i] as any;
                if ((msg?.message_type === "RESPONSE") && msg?.content && msg?.content?.length > 0) {
                  updated.push({ author: "ai", content: msg.content || "" });
                  break;
                }
              }
            }
            else {
              for(let i = response.messages.length - 1; i >= 0; i--){
                const msg = response.messages[i] as ChatMessageProto;
                if(chatMessageTypeFromJSON(msg.message_type) === (ChatMessageType.RESPONSE) && msg?.content && msg?.content?.length > 0){
                  updated.push({ author: "ai", content: msg.content || "" });
                }
                else if(chatMessageTypeFromJSON(msg.message_type) === (ChatMessageType.REASONING) && msg?.content && msg?.content?.length > 0){
                  updated.push({ author: "reasoning", content: "", artifacts: msg.artifacts });
                }
                else if(chatMessageTypeFromJSON(msg.message_type) === (ChatMessageType.HUMAN) && msg?.content && msg?.content?.length > 0){
                  if(msg.content === inputMsg){
                    break; // break the loop if the user message is the same as the input message
                  }
                }
              }
            }
					}

					return updated;
				});

				// Check if current chatId exists in sessions list, if not refresh the list
				if (chatId && fetchChatSessionsList && chatSessionsList) {
					const sessionExists = chatSessionsList.some(session => session.id === chatId);
					if (!sessionExists) {
						fetchChatSessionsList();
					}
				}
			} catch (error) {
				// Remove loading message on error
				setChatMessages(prev => prev.slice(0, -1));
				console.error("Error sending message:", error);
			} finally {
				setIsLoading(false);
			}
		}
  };

  return (
    <VStack w={isFromEvaPlayground ? "85%" : "100%"} h="95%" m={0}>
      <Box bg="bg.surface" minH="90%" borderRadius="md" w="95%">
        {isLoadingChat ? (
          <Box display="flex" alignItems="center" justifyContent="center" w="100%" h="100%">
            <Spinner size="md" color="purple.700" thickness="2px" mr={2} />
            <Text fontSize="md" color="gray.500" mb={0}>
              Loading...
            </Text>
          </Box>
        ) : (
          <Box
            ref={messagesContainerRef}
            display="flex"
            flexDirection="column"
            alignItems="flex-start"
            justifyContent="flex-start"
            w="100%"
            h="95%"
            overflow="auto"
            onScroll={handleScroll}
            p={1}
            css={{
              "&::-webkit-scrollbar": {
                display: "none"
              },
              scrollbarWidth: "none",
              msOverflowStyle: "none"
            }}
          >
            {chatMessages.length > 0 && (
              <Box flex="1" minH="0" />
            )}
            {chatMessages.length === 0 ? (
              <Box
                position="absolute"
                top="50%"
                left="50%"
                transform="translate(-50%, -50%)"
                width="90%"
                maxW="400px"
              >
                <Box
                  width="100%"
                  height="auto"
                  display="flex"
                  flexDirection="column"
                  bg="bg.surface"
                  px="6"
                  py="7"
                  cursor="default"
                >
                  <Text color="gray.700" fontSize="lg" fontWeight="medium" mb={0}>
                    How can I help?
                  </Text>
                </Box>
              </Box>
          ) : (
            chatMessages.map((msg, index) => {
              if (msg.author === "reasoning") {
                if (msg.artifacts && msg.artifacts.length > 0) {
                  // Show reasoning tree for artifacts from AI response
                  return (
                    <Box bg="bg.surface" height="90%" width="90%">
                      <ReasoningTreeContainer
                        key={index}
                        artifacts={msg.artifacts && msg.artifacts.length > 0 ? msg.artifacts : undefined}
                        nodeDataMap={nodeDataMap}
                        setNodeDataMap={setNodeDataMap}
                        onTableClick={onTableClick}
                        onFindingsClick={onFindingsClick}
                        onSynthesisClick={onSynthesisClick}
                        onContextualizationClick={onContextualizationClick}
                      />
                    </Box>
                  );
                }
              }

              // Regular user/AI messages
              return (
                <MessageComponent
                  key={index}
                  author={msg.author as "user" | "ai"}
                  content={msg.content}
                  isLoading={isLoading && index === chatMessages.length - 1 && msg.author === "ai"}
                  isLastMessage={index === chatMessages.length - 1}
                />
              );
            })
          )}
          <div ref={messagesEndRef} />
          </Box>
        )}
      </Box>
      <HStack w="95%" justify="space-between">
        <Textarea
          value={inputMessage}
          onChange={handleInputChange}
          onKeyDown={handleKeyPress}
          placeholder={!chatId ? "Please start a new chat or select an existing one" : "Ask me anything on your data"}
          resize="none"
          minHeight={20}
          maxHeight={100}
          flex={1}
          isDisabled={isLoadingChat || isLoading || !chatId}
          borderColor="gray.500"
          focusBorderColor="purple.100"
          fontSize="sm"
          m={1}
        />
        <IconButton
          icon={<Icon as={FaArrowUp} boxSize={4} color="purple.700" />}
          onClick={handleSendMessage}
          colorScheme="purple"
          bg="gray.100"
          isDisabled={isLoadingChat || isLoading || !chatId}
          aria-label={"Send Chat Message"}
          borderRadius="full"
          size="sm"
          _hover={{"bg": "purple.100", "color":"gray.700"}}
        />
      </HStack>
    </VStack>
  );
};

export default Chat;